/**
 * Stream'deki `redirection` payload'ını bu istemcinin tek etkisine çevirir.
 *
 * Yan etki yok: sayfa değiştirmez, modal açmaz. Yeni bir destination buraya
 * bir satır olarak eklenir; tanınmayan ad bir rotaya tahmin edilmez.
 */

export interface StreamNavigationRouteState {
  filters: Record<string, string[]>;
  fromChat: true;
}

export type StreamNavigation =
  | {
      kind: "route";
      url: string;
      state?: StreamNavigationRouteState;
    }
  | {
      kind: "product";
      productId: string;
      offerId: string | null;
    };

export interface RedirectionResolveOptions {
  /** Aktif commerce vitrini. Explore, bağlam yoksa buraya düşer. */
  shopPath: string;
}

const URL_BASE = "https://harmony.invalid";
const PRODUCT_PATH = /^\/fashion\/product\/([^/?#]+)$/;

const pageDestinations = (shopPath: string): Record<string, string> => ({
  checkout: "/checkout",
  explore: shopPath,
  orders: "/orders",
  profile: "/settings/profile",
  shopping: shopPath,
  add_address: "/checkout",
  address: "/checkout",
  cards: "/checkout",
  add_cards: "/checkout",
  summary: "/checkout",
});

const asNonEmptyString = (value: unknown): string | null =>
  typeof value === "string" && value.trim() !== "" ? value.trim() : null;

const toInternalUrl = (value: string): URL | null => {
  let parsed: URL;
  try {
    parsed = new URL(value, URL_BASE);
  } catch {
    return null;
  }

  if (parsed.origin !== URL_BASE) {
    if (typeof window === "undefined") return null;
    if (parsed.origin !== window.location.origin) return null;
  }

  return parsed;
};

const productIdFromPath = (pathname: string): string | null => {
  const match = pathname.match(PRODUCT_PATH);
  if (!match) return null;
  let productId: string;
  try {
    productId = decodeURIComponent(match[1]);
  } catch {
    return null;
  }
  if (productId === "" || productId === "." || productId === "..") return null;
  return productId;
};

const navigationFromUrl = (
  value: unknown,
  allowedPaths: Set<string>,
): StreamNavigation | null => {
  if (typeof value !== "string") return null;
  const parsed = toInternalUrl(value);
  if (!parsed) return null;

  const productId = productIdFromPath(parsed.pathname);
  if (productId) {
    return { kind: "product", productId, offerId: null };
  }

  if (!allowedPaths.has(parsed.pathname)) return null;
  return {
    kind: "route",
    url: `${parsed.pathname}${parsed.search}${parsed.hash}`,
  };
};

/**
 * `destination: "explore"` burada çözülmez. Keşif grubu (`exploreContext`)
 * çağıranın işidir; bağlam yoksa bu fonksiyon vitrin rotasını döndürür.
 */
export const resolveRedirection = (
  content: unknown,
  options: RedirectionResolveOptions,
): StreamNavigation | null => {
  if (!content || typeof content !== "object") return null;
  const raw = content as {
    url?: unknown;
    destination?: unknown;
    productId?: unknown;
    offerId?: unknown;
  };

  const pages = pageDestinations(options.shopPath);
  const allowedPaths = new Set([...Object.values(pages), "/shopping"]);
  const destination = asNonEmptyString(raw.destination);

  if (destination === "product") {
    const offerId = asNonEmptyString(raw.offerId);
    const productId = asNonEmptyString(raw.productId);
    if (productId) {
      return { kind: "product", productId, offerId };
    }
    const fromUrl = navigationFromUrl(raw.url, allowedPaths);
    if (fromUrl?.kind !== "product") return null;
    return { ...fromUrl, offerId };
  }

  if (destination && pages[destination]) {
    return { kind: "route", url: pages[destination] };
  }

  return navigationFromUrl(raw.url, allowedPaths);
};

/** `cart_update` bir sayfa adı taşımaz; sepet sayfasının kendisi hedeftir. */
export const cartPageNavigation = (): StreamNavigation => ({
  kind: "route",
  url: "/cart",
});
